<template>
  <section
    :role="variant === 'alert' ? 'alert' : 'status'"
    :class="[
      {
        'with-icon-and-button': showButton && leadingIcon,
        'with-icon-and-button': showButton && !leadingIcon,
        'with-icon-and-no-button': !showButton && leadingIcon
      },
      variant
    ]"
  >
    <div class="body">
      <div v-if="showLoadingIndicator" class="loading-indicator">
        <n-loading size="s" dark active />
      </div>
      <n-icon class="icon" :icon="icon" v-if="leadingIcon && icon" size="s" />
      <UserAvatar v-if="leadingAvatar" size="s" class="avatar" :user="user" />
      <span class="label">
        <div>{{ label }}</div>
        <div v-if="description" class="description">{{ description }}</div>
      </span>
    </div>

    <div class="actions" v-if="showActions">
      <div v-if="showButton" class="button-container">
        <n-button :label="buttonLabel" :disabled="isButtonDisabled" size="s" @click="onActionButtonClick" />
      </div>
      <n-icon v-if="!autoHide" class="close-button" icon="x" size="l" @click="onhide" />
    </div>
    <ContactBanner v-if="isContactBanner" :snackbarId="id" />
  </section>
</template>

<script lang="ts">
import User from '@/modules/user/models/User'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { mapStores } from 'pinia'
import { useSnackbarStore, SnackbarStore } from '@/modules/global/stores/SnackbarStore'
import UserAvatar from '@/modules/global/components/User/UserAvatar.vue'
import ContactBanner from '@/modules/global/components/ContactBanner.vue'

export type NSnackbarMeta = {
  spaceId?: string
}

export type NSnackbarProps = {
  id?: string
  label: string
  description?: string
  leadingIcon?: boolean
  leadingAvatar?: boolean
  user?: User
  variant: 'default' | 'success' | 'alert' | 'warning' | 'custom'
  autoHideDuration?: number | null
  autoHide?: boolean
  onClose?: () => void
  anchorOrigin?: { horizontal: 'center' | 'left' | 'right'; vertical: 'bottom' | 'top' }
  buttonLabel?: string
  isButtonDisabled?: boolean
  onButtonClick?: () => void
  meta?: NSnackbarMeta
  isContactBanner?: boolean
  showLoadingIndicator?: boolean
  dedupeKey?: string
}

@Component({
  computed: mapStores(useSnackbarStore),
  components: {
    UserAvatar,
    ContactBanner
  }
})
export default class NSnackbar extends Vue {
  @Prop() id!: NSnackbarProps['id']
  @Prop() label!: NSnackbarProps['label']
  @Prop() buttonLabel!: NSnackbarProps['buttonLabel']
  @Prop() isButtonDisabled!: NSnackbarProps['isButtonDisabled']
  @Prop() onButtonClick!: NSnackbarProps['onButtonClick']
  @Prop({ default: () => {} }) onClose!: NSnackbarProps['onClose']
  @Prop() description!: NSnackbarProps['description']
  @Prop({ default: true }) autoHide!: NSnackbarProps['autoHide']
  @Prop({ type: Boolean }) leadingIcon?: NSnackbarProps['leadingIcon']
  @Prop({ type: Boolean }) leadingAvatar?: NSnackbarProps['leadingAvatar']
  @Prop({ type: Boolean }) isContactBanner?: NSnackbarProps['isContactBanner']
  @Prop({ type: Boolean }) showLoadingIndicator?: NSnackbarProps['showLoadingIndicator']
  @Prop() user?: NSnackbarProps['user']
  @Prop() dedupeKey?: NSnackbarProps['dedupeKey']
  @Prop({
    default: 'default',
    validator: (value: string) => ['default', 'success', 'alert', 'warning', 'custom'].includes(value)
  })
  variant!: NSnackbarProps['variant']
  private snackbarStore!: SnackbarStore

  private get icon(): string | null {
    switch (this.variant) {
      case 'success':
        return 'check'
      case 'alert':
      case 'warning':
        return 'exclamation-circle'
      default:
        return null
    }
  }

  get showButton(): boolean {
    return !!this.buttonLabel && !!this.onButtonClick
  }

  get showActions(): boolean {
    return this.showButton || !this.autoHide
  }

  onActionButtonClick() {
    if (this.showButton && this.onButtonClick) {
      this.onButtonClick()
      this.onhide()
    }
  }

  onhide() {
    if (this.onClose) {
      this.onClose()
    }
    if (this.id) {
      this.snackbarStore.dequeue(this.id)
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

section {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: $notch-space-8x;

  min-height: $notch-space-12x;
  // A long unbroken label (e.g. a file name) used to stretch the toast across the screen
  max-width: min(480px, calc(100vw - #{$notch-space-8x}));
  box-sizing: border-box;

  border: 0;
  border-radius: $notch-radius-full;

  padding: calc($notch-space-4x - 1px) $notch-space-4x calc($notch-space-4x - 1px) $notch-space-4x;

  @include mobile {
    gap: $notch-space-2x;
  }

  &.with-icon-and-button {
    padding: $notch-space-2x $notch-space-4x !important;
  }

  &.no-icon-and-button {
    padding: $notch-space-2x $notch-space-4x $notch-space-2x $notch-space-4x !important;
  }

  &.with-icon-and-no-button {
    padding: calc($notch-space-4x - 1px) $notch-space-4x !important;
  }

  .loading-indicator {
    position: relative;
    margin-right: $notch-space-4x;
    margin-left: $notch-space-2x;
  }

  .body {
    display: inline-flex;
    align-items: center;
    min-width: 0;

    .icon {
      margin-right: $notch-space-2x;
    }

    .avatar {
      margin-right: $notch-space-2x;
    }
  }

  &.default {
    background-color: $notch-color-neutral-grey-600;

    .label,
    .close-button {
      color: $notch-color-neutral-grey-000;
    }
  }

  &.success {
    background-color: $notch-color-semantic-success-100;
  }

  &.alert {
    background-color: $notch-color-semantic-alert-100;
  }

  &.warning {
    background-color: $notch-color-semantic-accent-100;
  }

  &.custom {
    min-height: 0;
  }

  .label {
    @include notch-text-label-strong-M;
    min-width: 0;
    overflow-wrap: anywhere;

    @include mobile {
      padding-left: $notch-space-2x;
    }

    .description {
      @include notch-text-label-light-M;
    }
  }

  .actions {
    display: flex;
    align-items: center;
  }

  .button-container {
    margin-right: $notch-space-2x;
  }

  .close-button {
    cursor: pointer;
    height: $notch-space-4x;
    width: $notch-space-4x;
  }
}
</style>
